import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactECharts from 'echarts-for-react';
import { formatCurrency, formatMonth } from '../utils/format';
import { AV2_CHART } from '../utils/chartTheme';

interface DataPoint {
    month: string;
    revenue: number;
}

interface Props {
    data: DataPoint[];
    previousData?: DataPoint[] | null;
    locale?: string;
    /** Optional fixed height (px). When omitted the chart sizes itself to its container width. */
    height?: number;
}

// Responsive sizing: height tracks the container width so the chart keeps a
// pleasant aspect ratio on every screen, clamped to a sane band. At a ~880px
// card → ~352px tall; it grows to 360 on wide screens and never drops below 260
// on narrow ones / phones.
const HEIGHT_RATIO = 0.4;
const MIN_HEIGHT = 260;
const MAX_HEIGHT = 360;

/** Clamp container width → chart height. */
function heightForWidth(width: number): number {
    return Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, Math.round(width * HEIGHT_RATIO)));
}

/**
 * Returns a ref to attach to the chart's wrapper and a responsive height. If
 * `override` is given, that fixed height is used instead and no observer runs.
 */
function useResponsiveHeight(override?: number) {
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>(override ?? 320);

    useEffect(() => {
        if (override != null) {
            setHeight(override);
            return;
        }
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return;

        // Only update when the derived height actually changes — a no-op setState
        // would still re-render the chart and re-trigger the observer.
        const apply = (w: number) => {
            if (w <= 0) return;
            setHeight((prev) => {
                const next = heightForWidth(w);
                return next === prev ? prev : next;
            });
        };

        // Seed from the current width so there's no resize flash on mount.
        apply(el.clientWidth || 880);

        let raf = 0;
        const ro = new ResizeObserver((entries) => {
            // Defer to the next frame. Mutating layout synchronously inside the
            // observer callback is what makes the browser emit the (benign)
            // "ResizeObserver loop completed with undelivered notifications"
            // error that CRA's dev overlay surfaces as fatal.
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                if (entries.length) apply(entries[0].contentRect.width);
            });
        });
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [override]);

    return { ref, height };
}

/**
 * Monthly revenue line/area chart (ECharts) with an optional dashed overlay for
 * the previous period. Smooth curve, gradient fill, axis-trigger tooltip with
 * currency formatting, and responsive resize — all native to the library.
 * Props are unchanged from the previous hand-rolled SVG version so callers stay
 * the same. The previous period is index-aligned to the current months.
 */
const AreaChart: React.FC<Props> = ({ data, previousData, locale = 'en', height: heightOverride }) => {
    const intl = useIntl();
    const { ref, height } = useResponsiveHeight(heightOverride);

    const option = useMemo((): any => {
        const months = data.map((d) => formatMonth(d.month, locale));
        const curVals = data.map((d) => d.revenue);
        const hasPrev = !!previousData && previousData.length > 0;
        const prevVals = hasPrev
            ? data.map((_, i) => (previousData && previousData[i] ? previousData[i].revenue : null))
            : null;

        // Generic period labels are shared across the analytics page (same keys
        // the sales funnel uses) — these are plain "this period" / "previous".
        const thisLabel = intl.formatMessage({ id: 'analyticsV2.salesFunnel.thisPeriod', defaultMessage: 'this period' });
        const prevLabel = intl.formatMessage({ id: 'analyticsV2.salesFunnel.previousPeriod', defaultMessage: 'previous' });

        return {
            textStyle: { fontFamily: AV2_CHART.font },
            grid: { left: 4, right: 16, top: 16, bottom: hasPrev ? 30 : 6, containLabel: true },
            tooltip: {
                trigger: 'axis',
                backgroundColor: AV2_CHART.tooltipBg,
                borderWidth: 0,
                padding: [8, 12],
                textStyle: { color: '#ffffff', fontSize: 12, fontFamily: AV2_CHART.font },
                axisPointer: { type: 'line', lineStyle: { color: AV2_CHART.borderStrong, type: 'dashed', width: 1 } },
                formatter: (params: any[]) => {
                    if (!params || params.length === 0) return '';
                    const idx = params[0].dataIndex;
                    let html = `<div style="font-weight:600;margin-bottom:3px">${months[idx]}</div>`;
                    params.forEach((p) => {
                        if (p.value == null) return;
                        html +=
                            `<div style="display:flex;align-items:center;gap:6px">${p.marker}` +
                            `<span>${p.seriesName}</span>` +
                            `<span style="margin-left:auto;font-weight:600">${formatCurrency(Number(p.value), locale)}</span></div>`;
                    });
                    return html;
                },
            },
            legend: hasPrev
                ? {
                      bottom: 0,
                      data: [thisLabel, prevLabel],
                      icon: 'roundRect',
                      itemWidth: 11,
                      itemHeight: 11,
                      itemGap: 18,
                      textStyle: { color: AV2_CHART.textSecondary, fontSize: 12, fontFamily: AV2_CHART.font },
                  }
                : undefined,
            xAxis: {
                type: 'category',
                data: months,
                boundaryGap: false,
                axisTick: { show: false },
                axisLine: { lineStyle: { color: AV2_CHART.border } },
                axisLabel: { color: AV2_CHART.textTertiary, fontSize: 11, fontFamily: AV2_CHART.font },
            },
            yAxis: {
                type: 'value',
                splitNumber: 4,
                axisLabel: {
                    color: AV2_CHART.textTertiary,
                    fontSize: 11,
                    fontFamily: AV2_CHART.font,
                    formatter: (v: number) => formatCurrency(v, locale),
                },
                splitLine: { lineStyle: { color: AV2_CHART.border } },
            },
            series: [
                {
                    name: thisLabel,
                    type: 'line',
                    data: curVals,
                    smooth: true,
                    showSymbol: false,
                    symbolSize: 7,
                    lineStyle: { color: AV2_CHART.accent, width: 2.5 },
                    itemStyle: { color: AV2_CHART.accent },
                    emphasis: { focus: 'series' },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                { offset: 0, color: 'rgba(30,58,95,0.20)' },
                                { offset: 1, color: 'rgba(30,58,95,0.0)' },
                            ],
                        },
                    },
                    z: 3,
                },
                ...(prevVals
                    ? [
                          {
                              name: prevLabel,
                              type: 'line',
                              data: prevVals,
                              smooth: true,
                              showSymbol: false,
                              symbolSize: 7,
                              connectNulls: true,
                              lineStyle: { color: AV2_CHART.ghost, width: 1.5, type: 'dashed' },
                              itemStyle: { color: AV2_CHART.ghost },
                              z: 2,
                          },
                      ]
                    : []),
            ],
        };
    }, [data, previousData, locale, intl]);

    if (!data || data.length === 0) {
        return <div className="av2-chart-empty">—</div>;
    }

    return (
        <div className="av2-areachart" ref={ref}>
            <ReactECharts option={option} notMerge style={{ height, width: '100%' }} opts={{ renderer: 'svg' }} />
        </div>
    );
};

export default AreaChart;
